<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="constraints-page__title m-0">
          {{ $t('UserProjects.constraints.pageTitle') }}
        </h1>
        <p v-if="selectedProject?.name" class="constraints-page__subline m-0">
          {{ selectedProject.name }}
        </p>
      </div>
      <div v-if="userProjectContextReady && !loading" class="flex items-center gap-3 shrink-0">
        <Button
          :label="$t('UserProjects.constraints.createShort')"
          icon="pi pi-plus"
          class="btn-primary-accent"
          unstyled
          data-testid="constraints-create"
          @click="openCreateDialog"
        />
      </div>
    </div>

    <Message v-if="!hasProjectContext && userProjectContextReady" severity="info" :closable="false">
      {{ $t('UserProjects.context.required') }}
    </Message>

    <Message v-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div
      v-if="hasProjectContext && userProjectContextReady && !loading"
      class="flex flex-wrap items-center justify-between gap-3"
    >
      <div
        class="flex flex-wrap items-center gap-2"
        data-testid="constraints-status-filters"
      >
        <button
          v-for="option in statusFilterOptions"
          :key="option.value"
          type="button"
          class="constraints-page__pill"
          :class="{ 'constraints-page__pill--active': statusFilter === option.value }"
          :data-testid="`constraints-filter-${option.value}`"
          @click="statusFilter = option.value"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-3 ml-auto">
        <Select
          v-model="zeitraumFilter"
          :options="zeitraumOptions"
          option-label="label"
          option-value="value"
          class="constraints-page__select"
          data-testid="constraints-zeitraum-filter"
        />
        <Select
          v-model="milestoneFilter"
          :options="milestoneOptions"
          option-label="label"
          option-value="value"
          class="constraints-page__select"
          data-testid="constraints-milestone-filter"
        />
        <Select
          v-model="ownerFilter"
          :options="ownerOptions"
          option-label="label"
          option-value="value"
          class="constraints-page__select"
          data-testid="constraints-owner-filter"
        />
      </div>
    </div>

    <div v-if="loading || !userProjectContextReady" class="flex items-center justify-center py-16 text-[var(--color-disabled)]">
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('UserProjects.constraints.loading') }}
    </div>

    <div
      v-else-if="hasProjectContext && isEmpty"
      class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
    >
      <div class="font-medium text-[var(--color-dark)]">{{ $t('UserProjects.constraints.emptyTitle') }}</div>
      <div class="text-sm mt-1">{{ $t('UserProjects.constraints.emptyHint') }}</div>
    </div>

    <div
      v-else-if="hasProjectContext && filteredConstraints.length === 0"
      class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
    >
      <div class="font-medium text-[var(--color-dark)]">{{ $t('UserProjects.constraints.noMatchesTitle') }}</div>
      <div class="text-sm mt-1">{{ $t('UserProjects.constraints.noMatchesHint') }}</div>
    </div>

    <div
      v-else-if="hasProjectContext"
      class="flex flex-col gap-3"
      data-testid="constraints-list"
    >
      <ConstraintCard
        v-for="constraint in filteredConstraints"
        :key="constraint.id"
        :constraint="constraint"
        @select="openDrawer"
      />
    </div>

    <ConstraintDetailDrawer
      v-model:visible="drawerVisible"
      :constraint="selectedConstraint"
      @resolve="markResolved"
      @edit="openEditFromDrawer"
      @save-solution="saveSolution"
    />

    <ConstraintFormDialog
      v-model:visible="dialogVisible"
      :mode="dialogMode"
      :initial-constraint="editingConstraint"
      :project-name="selectedProject?.name || ''"
      :milestones="milestones"
      :participant-users="participantUsers"
      :default-owner-user-id="currentUserId"
      :saving="saving"
      @submit="submitForm"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Message from 'primevue/message';
import Select from 'primevue/select';
import ConstraintCard from '@/components/CustomerApp/UserProjects/ConstraintCard.vue';
import ConstraintDetailDrawer from '@/components/CustomerApp/UserProjects/ConstraintDetailDrawer.vue';
import ConstraintFormDialog from '@/components/CustomerApp/UserProjects/ConstraintFormDialog.vue';
import { resolveCurrentUserId } from '@/utils/resolveCurrentUserId';
import { displayConstraintTitle } from '@/utils/constraintFormMapping';
import {
  filterConstraintsByMilestone,
  filterConstraintsByOwner,
  filterConstraintsByStatus,
  filterConstraintsByZeitraum,
} from '@/utils/constraintDisplay';

export default {
  name: 'UserProjectsConstraintsView',
  components: {
    Button,
    Message,
    Select,
    ConstraintCard,
    ConstraintDetailDrawer,
    ConstraintFormDialog,
  },
  inject: {
    participantUsersProvider: { from: 'participantUsers', default: () => () => [] },
  },
  setup() {
    return {
      confirm: useConfirm(),
      toast: useToast(),
    };
  },
  data() {
    return {
      dialogVisible: false,
      drawerVisible: false,
      dialogMode: 'create',
      editingConstraint: null,
      selectedConstraint: null,
      saving: false,
      statusFilter: 'all',
      zeitraumFilter: 'all',
      milestoneFilter: 'all',
      ownerFilter: 'all',
    };
  },
  computed: {
    ...mapGetters('pmConstraint', {
      constraints: 'constraints',
      loading: 'loading',
      error: 'error',
      isEmpty: 'isEmpty',
    }),
    ...mapGetters('pmMilestone', {
      milestones: 'milestones',
    }),
    ...mapGetters('pmProject', {
      selectedProjectId: 'selectedProjectId',
      selectedProject: 'selectedProject',
      hasProjectContext: 'hasUserProjectContext',
      userProjectContextReady: 'userProjectContextReady',
    }),
    ...mapGetters(['getUser']),
    currentUserId() {
      return resolveCurrentUserId(this.getUser);
    },
    participantUsers() {
      return this.participantUsersProvider() || [];
    },
    statusFilterOptions() {
      return [
        { value: 'all', label: this.$t('UserProjects.constraints.filters.all') },
        { value: 'open', label: this.$t('UserProjects.constraints.filters.open') },
        { value: 'in_progress', label: this.$t('UserProjects.constraints.filters.inProgress') },
        { value: 'resolved', label: this.$t('UserProjects.constraints.filters.resolved') },
      ];
    },
    zeitraumOptions() {
      return [
        { value: 'all', label: this.$t('UserProjects.constraints.zeitraum.all') },
        { value: 'thisWeek', label: this.$t('UserProjects.constraints.zeitraum.thisWeek') },
        { value: 'thisMonth', label: this.$t('UserProjects.constraints.zeitraum.thisMonth') },
      ];
    },
    milestoneOptions() {
      return [
        { value: 'all', label: this.$t('UserProjects.constraints.milestoneFilter.all') },
        ...(this.milestones || []).map((item) => ({
          value: item.id,
          label: item.title,
        })),
      ];
    },
    ownerOptions() {
      const users = this.participantUsers || [];
      return [
        { value: 'all', label: this.$t('UserProjects.constraints.ownerFilter.all') },
        ...users.map((user) => ({
          value: user.id,
          label: user.name || user.email || user.id,
        })),
      ];
    },
    filteredConstraints() {
      let items = [...(this.constraints || [])];
      items = filterConstraintsByStatus(items, this.statusFilter);
      items = filterConstraintsByZeitraum(items, this.zeitraumFilter);
      items = filterConstraintsByMilestone(items, this.milestoneFilter);
      items = filterConstraintsByOwner(items, this.ownerFilter);
      return items;
    },
  },
  watch: {
    selectedProjectId() {
      this.resetFilters();
      this.refresh();
    },
    userProjectContextReady(isReady) {
      if (isReady) {
        this.refresh();
        this.tryOpenCreateFromRoute();
      }
    },
    '$route.query.create'(value) {
      if (value === '1') {
        this.tryOpenCreateFromRoute();
      }
    },
    loading(isLoading) {
      if (!isLoading) {
        this.tryOpenCreateFromRoute();
      }
    },
  },
  async mounted() {
    if (this.userProjectContextReady) {
      await this.refresh();
    }
    this.tryOpenCreateFromRoute();
    const routeFilter = this.$route.query.filter;
    if (typeof routeFilter === 'string' && ['all', 'open', 'in_progress', 'resolved'].includes(routeFilter)) {
      this.statusFilter = routeFilter;
    }
  },
  methods: {
    ...mapActions('pmConstraint', {
      fetchConstraints: 'fetchConstraints',
      setProjectContext: 'setProjectContext',
      createConstraint: 'createConstraint',
      updateConstraint: 'updateConstraint',
      resolveConstraint: 'resolveConstraint',
      deleteConstraint: 'deleteConstraint',
    }),
    ...mapActions('pmMilestone', {
      setMilestoneContext: 'setProjectContext',
    }),
    ...mapActions('pmCommitment', ['fetchUserProjectsOverview']),

    resetFilters() {
      this.statusFilter = 'all';
      this.zeitraumFilter = 'all';
      this.milestoneFilter = 'all';
      this.ownerFilter = 'all';
    },

    async refresh() {
      if (!this.userProjectContextReady) return;
      try {
        if (this.selectedProjectId) {
          await Promise.all([
            this.setProjectContext(this.selectedProjectId),
            this.setMilestoneContext(this.selectedProjectId),
          ]);
        }
      } catch {
        // handled in store
      }
    },

    tryOpenCreateFromRoute() {
      if (this.$route.query.create !== '1') return;
      if (!this.userProjectContextReady || !this.hasProjectContext || this.loading) return;
      this.openCreateDialog();
      this.$router.replace({ path: this.$route.path, query: {} });
    },

    async refreshOverview() {
      try {
        await this.fetchUserProjectsOverview();
      } catch {
        // overview errors are surfaced on overview page
      }
    },

    openCreateDialog() {
      if (!this.hasProjectContext) {
        this.toast.add({
          severity: 'warn',
          summary: this.$t('UserProjects.constraints.emptyTitle'),
          detail: this.$t('UserProjects.context.required'),
          life: 4000,
        });
        return;
      }
      this.dialogMode = 'create';
      this.editingConstraint = null;
      this.dialogVisible = true;
    },

    openDrawer(constraint) {
      this.selectedConstraint = constraint;
      this.drawerVisible = true;
    },

    openEditFromDrawer(constraint) {
      this.drawerVisible = false;
      this.openEditDialog(constraint);
    },

    openEditDialog(constraint) {
      this.dialogMode = 'edit';
      this.editingConstraint = constraint;
      this.dialogVisible = true;
    },

    async submitForm(payload) {
      this.saving = true;
      try {
        if (this.dialogMode === 'create') {
          await this.createConstraint(payload);
          this.toast.add({
            severity: 'success',
            summary: this.$t('ProjectManagement.constraints.toast.created'),
            life: 3000,
          });
        } else {
          await this.updateConstraint({ id: this.editingConstraint.id, payload });
          this.toast.add({
            severity: 'success',
            summary: this.$t('ProjectManagement.constraints.toast.updated'),
            life: 3000,
          });
        }
        this.dialogVisible = false;
        this.editingConstraint = null;
        await this.refreshOverview();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.constraints.toast.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },

    async saveSolution({ id, solutionText }) {
      try {
        const updated = await this.updateConstraint({
          id,
          payload: { solutionText },
        });
        if (this.selectedConstraint?.id === id) {
          this.selectedConstraint = updated;
        }
        this.toast.add({
          severity: 'success',
          summary: this.$t('UserProjects.constraints.drawer.solutionSaved'),
          life: 3000,
        });
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.constraints.toast.error'),
          life: 4000,
        });
      }
    },

    async markResolved(constraint) {
      try {
        await this.resolveConstraint(constraint.id);
        this.drawerVisible = false;
        this.selectedConstraint = null;
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.constraints.toast.resolved'),
          life: 3000,
        });
        await this.refreshOverview();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.constraints.toast.error'),
          life: 4000,
        });
      }
    },

    confirmDelete(constraint) {
      this.confirm.require({
        message: this.$t('ProjectManagement.constraints.deleteConfirm', {
          name: displayConstraintTitle(constraint),
        }),
        header: this.$t('ProjectManagement.constraints.deleteTitle'),
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await this.deleteConstraint(constraint.id);
            this.toast.add({
              severity: 'success',
              summary: this.$t('ProjectManagement.constraints.toast.deleted'),
              life: 3000,
            });
            await this.refreshOverview();
          } catch {
            this.toast.add({
              severity: 'error',
              summary: this.$t('ProjectManagement.constraints.toast.error'),
              life: 4000,
            });
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.constraints-page__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--color-dark);
}

.constraints-page__subline {
  margin-top: 0.125rem;
  font-size: 13px;
  color: var(--color-disabled);
}

.constraints-page__pill {
  padding: 0.375rem 0.875rem;
  border: none;
  border-radius: 999px;
  background: #f5f5f5;
  color: #6b6b6b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.constraints-page__pill:hover {
  background: #eeeeee;
}

.constraints-page__pill--active {
  background: linear-gradient(135deg, var(--color-orange) 0%, #f59e0b 100%);
  color: #fff;
}

.constraints-page__select {
  min-width: 10rem;
}
</style>
